import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const testimonials = [
  {
    quote: "Working with LOLCorp has been a great experience all around, every piece of software built so far has been of excellent quality and has drastically increased office productivity. The developers have always been very responsive and great to work with as well. We would absolutely recommend LOLCorp for any of your future software building needs, you won't regret it!",
    author: "Jeremi Gallant",
    role: "Director",
    company: "Integral Transfer Agency Inc.",
    result: "Internal tools built",
    image: "https://assets.lolcdn.xyz/client.png"
  },
  {
    quote: "LOLCorp built a custom internal dashboard for our operations team, and it completely changed how we track projects and performance. Ryan was especially great to work with. He understood our requirements quickly and suggested a few improvements we hadn't even thought of. Everything that used to live across multiple spreadsheets is now in one clean, fast system.",
    author: "Sarah K.",
    role: "Head of Operations",
    company: "TechSolutions",
    image: "https://assets.lolcdn.xyz/client.png",
    result: "Dashboard Built"
  },
  {
    quote: "We hired LOLCorp to build a secure internal CRM tailored to our sales workflow. Jack took the lead on our project and kept everything moving smoothly. Communication was clear and timelines were respected. The result is something our sales team genuinely enjoys using.",
    author: "Michael R.",
    role: "Sales Director",
    company: "NovaWorks",
    image: "https://assets.lolcdn.xyz/client.png",
    result: "Custom CRM"
  },
  {
    quote: "LOLCorp developed a custom finance and invoicing system for us that replaced three different tools we were paying for. Ryan was hands-on throughout the build and made sure the reporting logic matched our real business needs. The ROI was obvious within the first month.",
    author: "Priya M.",
    role: "Finance Lead",
    company: "InnoCore",
    image: "https://assets.lolcdn.xyz/client.png",
    result: "3 Tools Replaced"
  },
  {
    quote: "We needed an internal admin panel to manage users, permissions, and audit logs across our platform. Jack's attention to security details and edge cases really stood out. They delivered something clean, secure, and scalable.",
    author: "Daniel T.",
    role: "Founder",
    company: "BrightByte",
    image: "https://assets.lolcdn.xyz/client.png",
    result: "Secure Admin Panel"
  },
  {
    quote: "LOLCorp PVT LTD automated a big chunk of our internal workflows, approvals, and reporting. Ryan helped us rethink a few inefficient processes before building anything, which saved us a lot of time long-term. What used to take days now happens in minutes.",
    author: "Emma L.",
    role: "Operations Manager",
    company: "CloudForge",
    image: "https://assets.lolcdn.xyz/client.png",
    result: "Workflow Automation"
  },
  {
    quote: "We brought LOLCorp in to rebuild our internal tools from scratch after years of duct-taped solutions. Jack handled the technical architecture and made everything future-proof. Performance improved massively across the board.",
    author: "James P.",
    role: "COO",
    company: "Vertex Labs",
    image: "https://assets.lolcdn.xyz/client.png",
    result: "Tools Rebuilt"
  },
  {
    quote: "LOLCorp built a custom reporting and analytics system that gives us real-time insights into our business. Ryan stayed involved even after launch to fine-tune dashboards and KPIs. Leadership finally trusts the numbers they're seeing.",
    author: "Nina S.",
    role: "Operations Head",
    company: "BluePeak",
    image: "https://assets.lolcdn.xyz/client.png",
    result: "Analytics System"
  },
  {
    quote: "They didn't just build software for us, they solved actual business problems. Jack and Ryan both jumped in whenever something needed quick clarification. Support has been rock solid.",
    author: "Robert D.",
    role: "Managing Director",
    company: "AxisCore",
    image: "https://assets.lolcdn.xyz/client.png",
    result: "Problems Solved"
  },
  {
    quote: "Working with LOLCorp PVT LTD felt like working with an in-house engineering team. Ryan understood our requirements quickly, and Jack made sure the delivery stayed on track. They delivered exactly what we needed.",
    author: "Aisha Q.",
    role: "Product Lead",
    company: "ZenWorks",
    image: "https://assets.lolcdn.xyz/client.png",
    result: "On-Track Delivery"
  },
  {
    quote: "We needed a complex inventory and order tracking system, and Ryan made it happen without overcomplicating things. His ability to translate our messy requirements into clean, working software was impressive. The system has been running flawlessly for months now.",
    author: "Olivia T.",
    role: "Operations Manager",
    company: "GreenLeaf Supply",
    image: "https://assets.lolcdn.xyz/client.png",
    result: "Inventory System"
  },
  {
    quote: "Ryan developed an internal knowledge base and documentation system that finally got our scattered information organized. He suggested smart features we hadn't considered, like automated tagging and search improvements. Our team's productivity jumped noticeably after launch.",
    author: "Marcus V.",
    role: "Engineering Lead",
    company: "Diviner",
    image: "https://assets.lolcdn.xyz/client.png",
    result: "Knowledge Base"
  }
];

const caseStudies = [
  {
    title: "HR & Employee Management",
    description: "Complete internal HR system with employee records, leave management, and performance tracking",
    metric: "50% Time Saved",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop"
  },
  {
    title: "Operations Dashboard",
    description: "Real-time operations dashboard replacing multiple spreadsheets with unified insights",
    metric: "Single Source",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=400&fit=crop"
  },
  {
    title: "Finance & Invoicing System",
    description: "Custom invoicing and financial reporting replacing 3 separate tools",
    metric: "3x ROI",
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=600&h=400&fit=crop"
  }
];

export const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="mb-16 md:mb-24"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
            Client Success
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#003333] max-w-3xl">
            Trusted by Industry Leaders
          </h2>
        </motion.div>

        {/* Testimonial Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative bg-[#F8FAFC] p-8 md:p-16 mb-20"
        >
          <Quote className="absolute top-8 left-8 w-16 h-16 text-[#003333]/5" />
          
          <div className="relative z-10">
            <motion.blockquote
              key={activeTestimonial}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
              className="text-xl md:text-2xl lg:text-3xl leading-relaxed text-[#003333] mb-8 max-w-4xl"
            >
              "{testimonials[activeTestimonial].quote}"
            </motion.blockquote>
            
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <img 
                  src={testimonials[activeTestimonial].image}
                  alt={testimonials[activeTestimonial].author}
                  className="w-14 h-14 rounded-full object-cover bg-slate-200"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <div>
                  <p className="font-semibold text-[#003333] text-lg">
                    {testimonials[activeTestimonial].author}
                  </p>
                  <p className="text-slate-600">
                    {testimonials[activeTestimonial].role}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {testimonials[activeTestimonial].company}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="px-6 py-3 bg-[#003333] text-white rounded-full text-sm font-medium">
                  {testimonials[activeTestimonial].result}
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={prevTestimonial}
                    className="p-3 border border-slate-200 hover:border-[#003333] rounded-full transition-colors"
                    data-testid="prev-testimonial"
                  >
                    <ChevronLeft className="w-5 h-5 text-[#003333]" />
                  </button>
                  <button
                    onClick={nextTestimonial}
                    className="p-3 border border-slate-200 hover:border-[#003333] rounded-full transition-colors"
                    data-testid="next-testimonial"
                  >
                    <ChevronRight className="w-5 h-5 text-[#003333]" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Pagination dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === activeTestimonial 
                    ? 'bg-[#003333] w-8' 
                    : 'bg-slate-300 hover:bg-slate-400'
                }`}
                data-testid={`testimonial-dot-${index}`}
              />
            ))}
          </div>
        </motion.div>

        {/* Case Studies Grid */}
       {/*  <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#003333] mb-8">
            Featured Projects
          </h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.title}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                className="case-study-card group relative overflow-hidden bg-white border border-slate-100 hover:border-[#003333]/20 transition-all duration-500 card-hover"
                data-testid={`case-study-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#003333]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-6">
                    <span className="text-white font-bold text-3xl">{study.metric}</span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h4 className="font-semibold text-[#003333] text-lg mb-2">
                    {study.title}
                  </h4>
                  <p className="text-slate-600 text-sm">
                    {study.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};
